import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation, Outlet } from "react-router-dom";
import { FaBars, FaTimes, FaChevronDown } from "react-icons/fa";
import { ChevronDown } from "lucide-react";
import BottomNavbar from "../dashboard/BottomNavbar";
import { INFLUENCER_BASE } from "../constants";
import UserProfileMenu from "../../components/UserProfileMenu";

const EXPANDED_WIDTH = 250;
const COLLAPSED_WIDTH = 72;
const LARGE_SCREEN_QUERY = "(min-width: 1024px)";
const MEDIUM_SCREEN_QUERY = "(min-width: 768px)";

const getDefaultCollapsed = () =>
  typeof window !== "undefined" && !window.matchMedia(LARGE_SCREEN_QUERY).matches;

const getIsMdUp = () =>
  typeof window !== "undefined" && window.matchMedia(MEDIUM_SCREEN_QUERY).matches;

const InfluencerSidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [openMenu, setOpenMenu] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(getDefaultCollapsed);
  const [isMdUp, setIsMdUp] = useState(getIsMdUp);
  const sidebarRef = useRef(null);
  const iconOnly = isMdUp && isCollapsed;

  const base = INFLUENCER_BASE;

  // Sidebar items (paths under /influencer/dashboard when using InfluencerRoutes)
  const sidebarItems = [
    { 
      label: "Dashboard", 
      path: `${base}/dashboard`, 
      icon: "fas fa-home"
    },
    { 
      label: "Connect Social Media", 
      path: `${base}/dashboard/connect-social-media`, 
      icon: "fas fa-check-circle"
    },
    { 
      label: "Generate Affiliate Link", 
      path: `${base}/dashboard/generate-link`, 
      icon: "fas fa-link"
    },
    { 
      label: "Link Management", 
      path: `${base}/dashboard/link-management`, 
      icon: "fas fa-list"
    },
    { 
      label: "Campaigns", 
      icon: "fas fa-briefcase",
      subItems: [
        {
          label: "Your Campaigns",
          path: `${base}/dashboard/affiliate-dashboard`,
        },
        {
          label: "Advertising Campaigns",
          path: "/campaign",
        },
        {
          label: "My Applications",
          path: `${base}/dashboard/my-applications`,
        }
      ]
    },
    {
      label: "Media",
      icon: "fas fa-photo-video",
      subItems: [
        { 
          label: "YouTube Media", 
          path: `${base}/dashboard/youtube-media-dashboard`
        },
        { 
          label: "Instagram Media", 
          path: `${base}/dashboard/insta-media-dashboard`
        }
      ],
    },
    {
      label: "Automation",
      icon: "fas fa-robot",
      subItems: [
        {
          label: "Automation Dashboard",
          path: `${base}/dashboard/automation-dashboard`,
        },
        {
          label: "Instagram Automation",
          path: `${base}/dashboard/instagram-automation`,
        }
      ],
    },
    { 
      label: "Service Pricing", 
      path: `${base}/dashboard/service-payment`, 
      icon: "fas fa-credit-card"
    },
    { 
      label: "Earnings", 
      path: `${base}/dashboard/earnings`, 
      icon: "fas fa-dollar-sign"
    },
    { 
      label: "Bank Details", 
      path: `${base}/dashboard/bank`, 
      icon: "fas fa-university"
    },
  ];

// Auto-expand relevant submenu based on current page
useEffect(() => {
  const mediaMenuIndex = sidebarItems.findIndex((item) => item.label === "Media");
  const automationMenuIndex = sidebarItems.findIndex((item) => item.label === "Automation");

  if (
    location.pathname.includes("/youtube-media-dashboard") ||
    location.pathname.includes("/insta-media-dashboard")
  ) {
    setOpenMenu(mediaMenuIndex);
  } else if (
    location.pathname.includes("/automation-dashboard") ||
    location.pathname.includes("/instagram-automation")
  ) {
    setOpenMenu(automationMenuIndex);
  }
}, [location.pathname]);

  useEffect(() => {
    const largeMedia = window.matchMedia(LARGE_SCREEN_QUERY);
    const mediumMedia = window.matchMedia(MEDIUM_SCREEN_QUERY);
    const handleLargeChange = (event) => setIsCollapsed(!event.matches);
    const handleMediumChange = (event) => setIsMdUp(event.matches);
    largeMedia.addEventListener("change", handleLargeChange);
    mediumMedia.addEventListener("change", handleMediumChange);
    return () => {
      largeMedia.removeEventListener("change", handleLargeChange);
      mediumMedia.removeEventListener("change", handleMediumChange);
    };
  }, []);

  // Handle clicks outside sidebar to close it on mobile
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sidebarOpen && sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        setSidebarOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [sidebarOpen]);

  const handleNavigation = (item) => {
    if (item.path) {
      navigate(item.path);
      // Close sidebar on mobile after navigation
      setSidebarOpen(false);
    }
  };

  // Function to check if a path is active
  const isPathActive = (path) => {
    return location.pathname === path;
  };

  // Function to check if a submenu item is active
  const isSubItemActive = (subItems) => {
    return subItems?.some(subItem => isPathActive(subItem.path));
  };

  // Function to get link classes with active state
  const getLinkClasses = (item) => {
    const isActive = item.subItems ? isSubItemActive(item.subItems) : isPathActive(item.path);
    return `relative flex items-center cursor-pointer rounded-lg transition-all duration-200 ${
      iconOnly ? "justify-center px-2 py-2.5" : "justify-between px-3 py-2.5"
    } ${
      isActive
        ? "bg-orange-100 text-orange-600 font-semibold"
        : "text-gray-700 hover:bg-orange-50 hover:text-orange-600"
    }`;
  };

  // Function to get submenu item classes with active state
  const getSubItemClasses = (subItem) => {
    const isActive = isPathActive(subItem.path);
    return `py-2 px-4 text-sm cursor-pointer rounded-lg transition-all duration-200 ${
      isActive 
        ? "bg-orange-50 text-orange-600 font-semibold shadow-sm border-l-2 border-orange-400" 
        : "text-gray-600 hover:bg-orange-50 hover:text-orange-600 hover:shadow-sm"
    }`;
  };

  // Function to get page title based on current path
  const getPageTitle = () => {
    const path = location.pathname;
    if (path.endsWith('/dashboard') || path.endsWith('/dashboard/')) return 'Dashboard';
    if (path.includes('connect-social-media')) return 'Connect Social Media';
    if (path.includes('generate-link')) return 'Generate Affiliate Link';
    if (path.includes('my-applications')) return 'My Applications';
    if (path.includes('affiliate-dashboard')) return 'Campaign Details';
    if (path.includes('youtube-media-dashboard')) return 'YouTube Media';
    if (path.includes('insta-media-dashboard')) return 'Instagram Media';
    if (path.includes("automation-dashboard")) return "Automation Dashboard";
    if (path.includes("instagram-automation")) return "Instagram Automation";
    if (path.includes('service-payment')) return 'Service Pricing';
    if (path.includes('earnings')) return 'Earnings';
    if (path.includes('/bank')) return 'Bank Details';
    if (path.includes('subscription-billing')) return 'Subscription & Billing';
    if (path.includes('influencer-profile')) return 'Profile';
    if (path.includes('settings')) return 'Settings';
    if (path.includes('link-management')) return 'Link Management';
    return 'Dashboard';
  };

  return (
    <div className="h-[calc(100dvh-80px)] bg-gray-100 flex flex-col overflow-hidden">
      {/* Main Content Area */}
      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* Mobile Sidebar Overlay */}
        {sidebarOpen && (
          <div className="fixed inset-x-0 top-[70px] bottom-0 bg-black bg-opacity-50 z-[125] md:hidden" />
        )}

        {/* Sidebar - Fixed on left */}
        <div
          ref={sidebarRef}
          className={`
            fixed top-[70px] left-0 bottom-0 bg-white shadow-xl
            transform transition-all duration-300 ease-in-out
            md:translate-x-0 md:shadow-lg md:border-r md:border-gray-200 md:z-40
            ${sidebarOpen ? "translate-x-0 z-[130]" : "-translate-x-full z-40"}
          `}
          style={{ width: iconOnly ? COLLAPSED_WIDTH : EXPANDED_WIDTH }}
        >
          <div className="relative w-full h-full min-h-0 bg-white flex flex-col overflow-visible">
            <button
              type="button"
              onClick={() => {
                setIsCollapsed((prev) => {
                  if (!prev) setOpenMenu(null);
                  return !prev;
                });
              }}
              className="absolute top-4 -right-3 z-50 hidden h-7 w-7 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-500 shadow-sm transition-colors hover:bg-gray-50 hover:text-gray-800 md:flex"
              aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              <ChevronDown
                className={`h-3.5 w-3.5 ${isCollapsed ? "-rotate-90" : "rotate-90"}`}
              />
            </button>

            {/* Mobile close button */}
            <div className="flex-shrink-0 flex justify-end px-4 pt-3 md:hidden">
              <button
                onClick={() => setSidebarOpen(false)}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <FaTimes className="w-5 h-5 text-gray-600" />
              </button>
            </div>

            {/* Sidebar Navigation - Scrollable */}
            <nav className={`flex-1 min-h-0 overflow-y-auto pb-6 space-y-1 ${iconOnly ? "px-2 pt-8" : "p-3 pt-8 md:pt-10"}`}>
              {sidebarItems.map((item, index) => (
                <div key={index} className="relative mb-1">
                  <div
                    onClick={() => {
                      if (item.subItems) {
                        if (iconOnly) {
                          setIsCollapsed(false);
                          setOpenMenu(index);
                          return;
                        }
                        setOpenMenu(openMenu === index ? null : index);
                        return;
                      }
                      handleNavigation(item);
                    }}
                    className={getLinkClasses(item)}
                    title={iconOnly ? item.label : undefined}
                  >
                    <div className={`flex items-center min-w-0 ${iconOnly ? "" : "space-x-3"}`}>
                      <i className={`${item.icon} text-gray-600 w-5 text-center shrink-0`}></i>
                      {!iconOnly ? (
                        <span className="text-gray-700 font-medium truncate">{item.label}</span>
                      ) : null}
                    </div>
                    {item.subItems && !iconOnly ? (
                      <FaChevronDown 
                        className={`w-4 h-4 text-gray-500 shrink-0 transition-transform duration-200 ${
                          openMenu === index ? 'rotate-180' : ''
                        }`}
                      />
                    ) : null}
                  </div>
                  {item.subItems && openMenu === index && !iconOnly && (
                    <div className="ml-8 mt-2 space-y-1">
                      {item.subItems.map((subItem, subIndex) => (
                        <div
                          key={subIndex}
                          onClick={() => handleNavigation(subItem)}
                          className={getSubItemClasses(subItem)}
                        >
                          {subItem.label}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </nav>

            {/* Profile card - Bottom */}
            <div className={`flex-shrink-0 border-t border-gray-200 ${iconOnly ? "p-2" : "p-3"}`}>
              <UserProfileMenu
                variant="sidebar"
                className="w-full"
                showDashboardAction={false}
                collapsed={iconOnly}
                closeMobileSidebar={() => setSidebarOpen(false)}
              />
            </div>
          </div>
        </div>

        {/* Main Content - Right side section */}
        <div
          className={`flex-1 flex flex-col min-h-0 overflow-hidden transition-all duration-300 ease-in-out ${
            iconOnly ? "md:ml-[72px]" : "md:ml-[250px]"
          }`}
        >
          {/* Mobile Header with Sidebar Toggle */}
          <div className="md:hidden fixed top-[70px] left-0 right-0 z-30 w-full bg-white border-b border-gray-200 px-4 py-3 shadow-sm">
            <div className="flex items-center justify-between">
              <button
                onClick={() => setSidebarOpen(true)}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <FaBars className="w-5 h-5 text-gray-600" />
              </button>
              <h1 className="text-sm md:text-lg lg:text-lg font-semibold text-gray-800">{getPageTitle()}</h1>
              <div className="w-10"></div>
            </div>
          </div>

          <main className="flex flex-1 min-h-0 flex-col overflow-y-auto pt-12 md:pt-0 pb-20 md:pb-6 bg-gray-50">
            <Outlet />
          </main>
        </div>
      </div>
      
      {/* Bottom Navigation Bar - Only visible on mobile */}
      <BottomNavbar />
    </div>
  );
};

export default InfluencerSidebar;
